import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { PushSubscribeDto } from '@shared-types';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';

const PROMPT_DISMISSED_KEY = 'sm_display_push_prompt_dismissed';

/** Browser-Push: Banner, Berechtigung (user gesture), Subscription. */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly authService = inject(AuthService);

  private get apiUrl(): string {
    return this.configService.getConfig().apiUrl;
  }

  /**
   * Nach Login: bestehende Erlaubnis nutzen (ohne Dialog).
   * Chrome zeigt requestPermission() nur nach Nutzerklick – siehe enablePushFromUserGesture().
   */
  async syncPushSubscriptionIfGranted(): Promise<void> {
    if (!this.authService.canReceivePush() || !this.isSupported()) return;
    if (Notification.permission !== 'granted') return;
    try {
      await this.registerSubscription();
    } catch (err) {
      console.warn('Push-Subscription konnte nicht registriert werden:', err);
    }
  }

  /** Banner anzeigen, wenn noch keine Entscheidung und nicht weggeklickt. */
  shouldShowPushPrompt(): boolean {
    if (!this.authService.canReceivePush() || !this.isSupported()) return false;
    if (Notification.permission !== 'default') return false;
    return localStorage.getItem(PROMPT_DISMISSED_KEY) !== '1';
  }

  dismissPushPrompt(): void {
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  }

  /** Muss aus einem Klick-Handler aufgerufen werden (Chrome user gesture). */
  async enablePushFromUserGesture(): Promise<'granted' | 'denied' | 'no-vapid' | 'error'> {
    if (!this.isSupported()) return 'error';

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (permission === 'denied') {
        this.dismissPushPrompt();
      }
      return 'denied';
    }

    try {
      await this.registerSubscription();
      this.dismissPushPrompt();
      return 'granted';
    } catch (err) {
      console.warn('Push-Aktivierung fehlgeschlagen:', err);
      const message = String((err as Error)?.message ?? err);
      if (message.includes('publicKey') || message.includes('null')) {
        return 'no-vapid';
      }
      return 'error';
    }
  }

  isSupported(): boolean {
    return (
      typeof window !== 'undefined'
      && 'Notification' in window
      && 'serviceWorker' in navigator
      && 'PushManager' in window
    );
  }

  async registerSubscription(): Promise<void> {
    if (!this.isSupported()) return;

    const { publicKey } = await firstValueFrom(
      this.http.get<{ publicKey: string | null }>(`${this.apiUrl}/push/vapid-public-key`),
    );
    if (!publicKey) {
      throw new Error('VAPID publicKey nicht konfiguriert (Backend)');
    }

    const registration = await navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicKey),
      });
    }

    const body = this.subscriptionToDto(subscription);
    await firstValueFrom(this.http.post(`${this.apiUrl}/push/subscribe`, body));
  }

  private subscriptionToDto(subscription: PushSubscription): PushSubscribeDto {
    const json = subscription.toJSON();
    return {
      endpoint: json.endpoint!,
      keys: {
        p256dh: json.keys!['p256dh']!,
        auth: json.keys!['auth']!,
      },
    };
  }

  private urlBase64ToUint8Array(base64String: string): BufferSource {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      output[i] = raw.charCodeAt(i);
    }
    return output;
  }
}
